import { VariantProps, cva } from 'class-variance-authority'
import { ChevronRightIcon } from 'lucide-react'

import { cn } from '@/lib/ui/style-helpers'

import { Button, ButtonProps } from '@/components/lemmala-design-system/button/button'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

const overviewCardPropsVariants = cva(
  'flex flex-1 flex-col border justify-between rounded-lg p-4 shadow-sm',
  {
    variants: {
      variant: {
        default: 'bg-main-background-card',
        disabled: 'border text-main-foreground-disabled bg-main-background-subtle-gray',
        danger: 'bg-main-background-card text-main-foreground-danger',
        success: 'bg-main-background-card text-main-foreground-success',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type OverviewCardProps = {
  actionVariant?: ButtonProps['variant']
  title: string
  mainNumber?: React.ReactNode
  details: React.ReactNode[] | React.ReactNode
  action: {
    label: string
    onClick?: () => void
  }
  variant?: VariantProps<typeof overviewCardPropsVariants>['variant']
  tooltip?: React.ReactNode
  buttonClassName?: ButtonProps['className']
}

export const OverviewCard = ({
  title,
  mainNumber,
  details,
  action,
  variant,
  actionVariant = 'ghost',
  tooltip,
  buttonClassName,
}: OverviewCardProps) => {
  return (
    <div className={overviewCardPropsVariants({ variant })}>
      <Tooltip
        tooltipDisabled={!tooltip}
        triggerClassName='flex items-start flex-col'
        trigger={
          <>
            <div className='text-main-foreground-primary text-label-large'>{title}</div>
            {Array.isArray(details) ? (
              <>
                <div className='my-3 text-heading-xlarge'>{mainNumber}</div>
                <div className='flex flex-col items-start gap-1'>
                  {details.map((detail, index) => (
                    <div className='text-label-small' key={index}>
                      {detail}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className='my-3 text-heading-xlarge'>{details}</div>
            )}
          </>
        }
      >
        {tooltip}
      </Tooltip>

      <div>
        <Separator className='my-4' />
        <Button
          disabled={!action.onClick}
          variant={actionVariant}
          onClick={action.onClick}
          className={cn(
            'w-full',
            actionVariant === 'ghost' && 'text-main-foreground-accent',
            buttonClassName,
          )}
        >
          <TextWithIcon
            className='w-full justify-between'
            iconPosition='end'
            text={<div className='truncate'>{action.label}</div>}
            icon={<ChevronRightIcon className='size-4' />}
          />
        </Button>
      </div>
    </div>
  )
}
